import React from "react";
import { Box, Typography } from "@mui/material";
import { formatToBRL } from "../../utils/currency.util";
import { TOL_DIVERGENCIA_CAPA } from "./capaSaldo.util";
import styles from "./styles.module.css";

function formatSaldo(val) {
  if (val === null || val === undefined || val === "") {
    return "—";
  }
  const n = Number(val);
  if (!Number.isFinite(n)) {
    return "—";
  }
  return formatToBRL(n);
}

/** Fallback legado: fechamento da importação em validacaoExtracao. */
function resolveSaldoConciliadoLegacy(extrato) {
  const val = extrato?.validacaoExtracao;
  if (!val || typeof val !== "object") {
    return null;
  }
  if (val.saldoCalculado !== null && val.saldoCalculado !== undefined) {
    return val.saldoCalculado;
  }
  const anterior =
    val.saldoAnterior ?? extrato?.saldoAnterior ?? null;
  if (
    anterior !== null &&
    anterior !== undefined &&
    val.variacaoLiquida !== null &&
    val.variacaoLiquida !== undefined
  ) {
    return Number(anterior) + Number(val.variacaoLiquida);
  }
  return null;
}

function resolveDiferencaLegacy(extrato) {
  const val = extrato?.validacaoExtracao;
  if (!val || typeof val !== "object") {
    return { texto: "—", status: "neutro" };
  }
  const { divergencia, fechamentoOk } = val;
  if (fechamentoOk === true) {
    return { texto: formatToBRL(0), status: "ok" };
  }
  if (fechamentoOk === false && divergencia !== null && divergencia !== undefined) {
    const n = Number(divergencia);
    if (Number.isFinite(n)) {
      return { texto: formatToBRL(n), status: "erro" };
    }
  }
  if (divergencia !== null && divergencia !== undefined) {
    const n = Number(divergencia);
    if (Number.isFinite(n) && Math.abs(n) <= TOL_DIVERGENCIA_CAPA) {
      return { texto: formatToBRL(0), status: "ok" };
    }
    if (Number.isFinite(n)) {
      return { texto: formatToBRL(n), status: "erro" };
    }
  }
  return { texto: "—", status: "neutro" };
}

function statusPorDiferenca(diferenca) {
  if (diferenca === null || diferenca === undefined) {
    return { texto: "—", status: "neutro" };
  }
  const n = Number(diferenca);
  if (!Number.isFinite(n)) {
    return { texto: "—", status: "neutro" };
  }
  if (Math.abs(n) <= TOL_DIVERGENCIA_CAPA) {
    return { texto: formatToBRL(0), status: "ok" };
  }
  return { texto: formatToBRL(n), status: "erro" };
}

function ItemSaldo({ rotulo, valor, valorClassName }) {
  return (
    <Box className={styles.capaSaldoItem}>
      <Typography component="span" className={styles.capaSaldoRotulo}>
        {rotulo}
      </Typography>
      <Typography
        component="span"
        className={valorClassName || styles.capaSaldoValor}
      >
        {valor}
      </Typography>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {object} props.extrato — metadados do extrato (fallback de saldos)
 * @param {object|null} [props.capaSaldo] — projeção da API (Σ linhas da grade)
 */
export default function CapaSaldoConciliacao({ extrato, capaSaldo = null }) {
  if (!extrato || extrato.idConciliacaoExtrato == null) {
    return null;
  }

  const temCapaApi = capaSaldo && typeof capaSaldo === "object";
  const temSaldo =
    temCapaApi ||
    extrato.saldoAnterior != null ||
    extrato.saldoAtual != null ||
    (extrato.validacaoExtracao && typeof extrato.validacaoExtracao === "object");

  if (!temSaldo) {
    return (
      <Typography className={styles.capaSaldoIndisponivel} variant="body2">
        Saldos do extrato não disponíveis para este lançamento.
      </Typography>
    );
  }

  const saldoAnterior = temCapaApi
    ? capaSaldo.saldoAnterior ?? extrato.saldoAnterior
    : extrato.saldoAnterior;
  const saldoAtual = temCapaApi
    ? capaSaldo.saldoAtual ?? extrato.saldoAtual
    : extrato.saldoAtual;
  const saldoConciliado = temCapaApi
    ? capaSaldo.saldoConciliado
    : resolveSaldoConciliadoLegacy(extrato);
  const { texto: textoDiferenca, status: statusDiferenca } = temCapaApi
    ? statusPorDiferenca(capaSaldo.diferenca)
    : resolveDiferencaLegacy(extrato);

  const classeDiferenca =
    statusDiferenca === "ok"
      ? styles.capaSaldoDiferencaOk
      : statusDiferenca === "erro"
        ? styles.capaSaldoDiferencaErro
        : styles.capaSaldoDiferencaNeutro;

  return (
    <Box className={styles.capaSaldoConciliacao}>
      <Box className={styles.capaSaldoLinha}>
        <ItemSaldo
          rotulo="Saldo anterior"
          valor={formatSaldo(saldoAnterior)}
        />
        <ItemSaldo
          rotulo="Saldo atual"
          valor={formatSaldo(saldoAtual)}
        />
      </Box>
      <Box className={styles.capaSaldoLinha}>
        <ItemSaldo
          rotulo="Saldo conciliado"
          valor={formatSaldo(saldoConciliado)}
        />
        <ItemSaldo
          rotulo="Diferença"
          valor={textoDiferenca}
          valorClassName={classeDiferenca}
        />
      </Box>
    </Box>
  );
}
